import { useEffect, useMemo } from "react";
import { FormLabel, FormMessage } from "@/components/ui/form";
import { CustomerType, TenderType } from "@/graphql/__generated__/graphql";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import {
  HiddenInputField,
  InputField,
  RadioInputField,
  TextareaInputField,
  CheckboxInputField,
  ComboboxInputField,
} from "@/components/shared/form";
import { provinces } from "@/constants/provinces";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useSearchParams } from "react-router-dom";
import OperationalOrganizationsFormGroup from "../../Shared/FormGroups/OperationalOrganizationsFormGroup";
import { CustomerNameComponent } from "../../Shared/CustomerNameComponent";
import { nonEmptyString, optionalString } from "@/utils/form";
import { cn } from "@/lib/utils";
import { getInputWidth } from "../../Shared/helpers";

export const stringToNumber = (val: unknown) => (typeof val === "string" ? parseFloat(val) : val);

export const createDescriptionSchema = (t: TFunction) => {
  return z.object({
    description: z.object({
      name: z.string().min(2, {
        message: t("schemas:createtender.description.namemin"),
      }).max(75, {
        message: t("schemas:createtender.description.namemax"),
      }),
      customerName: z.string().min(2, {
        message: t("schemas:createtender.description.customernamemin"),
      }).optional().or(z.literal("")),
      customerId: z.number().optional().or(z.literal(NaN)),
      province: nonEmptyString(t("newtender.formgroups.description.inputs.province.label")),
      ownerId: z.number(),
      type: z.enum([TenderType.Public, TenderType.Private], {
        message: t("schemas:createtender.description.typerequired"),
      }),
      platform: optionalString(t("newtender.formgroups.description.inputs.platform.label")),
      awardingAuthority: optionalString(t("newtender.formgroups.description.inputs.awardingauthority.label")),
      customerType: z.enum([CustomerType.Existingcustomer, CustomerType.Newservice, CustomerType.Newcustomer], { // TODO: CustomerType.ExistingCustomerNewSite, Waiting for Backend
        message: t("schemas:createtender.description.customertyperequired"),
      }),
      details: z
        .string()
        .max(500, {
          message: t("schemas:createtender.description.detailsmax"),
        }),
      permanent: z.boolean(),
      mobile: z.boolean(),
      alarm: z.boolean(),
    }).check((ctx) => {
      // Service type validation - at least one must be selected
      if (!ctx.value.permanent && !ctx.value.mobile && !ctx.value.alarm) {
        // Attach error to all three service type fields
        ctx.issues.push({
          code: "custom",
          message: t("schemas:createtender.description.servicetypemin"),
          path: ["permanent"],
          input: ctx.value.permanent,
        });
        ctx.issues.push({
          code: "custom",
          message: t("schemas:createtender.description.servicetypemin"),
          path: ["mobile"],
          input: ctx.value.mobile,
        });
        ctx.issues.push({
          code: "custom",
          message: t("schemas:createtender.description.servicetypemin"),
          path: ["alarm"],
          input: ctx.value.alarm,
        });
      }
      if (!ctx.value.customerName && !ctx.value.customerId) {
        ctx.issues.push({
          code: "custom",
          message: t("schemas:createtender.description.customerrequired"),
          path: ["customerName"],
          input: ctx.value.customerName,
        });
      }

      if (ctx.value.type === TenderType.Public && !ctx.value.platform) {
        ctx.issues.push({
          code: "custom",
          message: t("schemas:createtender.description.platformmin"),
          path: ["platform"],
          input: ctx.value.platform,
        });
      }

      if (ctx.value.type === TenderType.Public && !ctx.value.awardingAuthority) {
        ctx.issues.push({
          code: "custom",
          message: t("schemas:form.required"),
          path: ["awardingAuthority"],
          input: ctx.value.awardingAuthority,
        });
      }
    })
  });
};

export const descriptionDefaultValues = {
  description: {
    name: "",
    customerName: "",
    customerId: undefined,
    province: undefined,
    ownerId: undefined,
    type: undefined,
    platform: undefined,
    awardingAuthority: undefined,
    customerType: undefined,
    details: "",
    permanent: false,
    mobile: false,
    alarm: false,
  }
};

export function DescriptionFormGroup({ form, prefilled, leftPanelWidth }: { form: UseFormReturn<any>, prefilled: boolean, leftPanelWidth: number }) {
  const { formState } = form;

  const [tenderType, permanent, mobile, alarm] = form.watch([
    "description.type",
    "description.permanent",
    "description.mobile",
    "description.alarm"
  ]);

  const { serviceTypeError, serviceTypeMessage } = useMemo(() => {
    const permanentError = (formState.errors.description as any)?.permanent;
    const mobileError = (formState.errors.description as any)?.mobile;
    const alarmError = (formState.errors.description as any)?.alarm;
    const hasError = permanentError?.type === "custom" || mobileError?.type === "custom" || alarmError?.type === "custom";
    const message = permanentError?.message || mobileError?.message || alarmError?.message;
    return { serviceTypeError: hasError, serviceTypeMessage: message };
  }, [formState.errors.description]);

  // Hooks
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const aiImportID = searchParams.get("importID");

  // Handle tender type changes
  useEffect(() => {
    if (tenderType === TenderType.Private) {
      // Clear errors for platform when type is private
      form.clearErrors("description.platform");
      // Reset the platform field value
      form.setValue("description.platform", undefined);
    }
  }, [tenderType, form.clearErrors, form.setValue]);

  // Handle service type validation
  useEffect(() => {
    if (permanent || mobile || alarm) {
      // Clear errors for all service type fields when at least one is selected
      form.clearErrors("description.permanent");
      form.clearErrors("description.mobile");
      form.clearErrors("description.alarm");
    } else {
      // When no service types are selected, trigger validation to show errors on all fields
      form.trigger(["description.permanent", "description.mobile", "description.alarm"]);
    }
  }, [permanent, mobile, alarm, form.clearErrors, form.trigger]);

  return (
    <div id="description">
      <div className="form-field-container">
        <InputField
          form={form}
          name="description.name"
          label={t("newtender.formgroups.description.inputs.name.label")}
          placeholder={t("newtender.formgroups.description.inputs.name.placeholder")}
          description={t("newtender.formgroups.description.inputs.name.description")}
          className={cn(getInputWidth(leftPanelWidth))}
        />

        <OperationalOrganizationsFormGroup form={form} className={cn(getInputWidth(leftPanelWidth))} />

        <CustomerNameComponent
          form={form}
          formPrefix="description"
          prefilled={prefilled}
          aiImportID={aiImportID}
          className={cn(getInputWidth(leftPanelWidth))}
        />

        <HiddenInputField form={form} name="description.customerId" />

        <ComboboxInputField
          form={form}
          name="description.province"
          label={t("newtender.formgroups.description.inputs.province.label")}
          options={provinces.map((province) => ({
            label: province.name,
            value: province.code
          }))}
          className={cn(getInputWidth(leftPanelWidth))}
        />

        <RadioInputField
          form={form}
          name="description.type"
          label={t("newtender.formgroups.description.inputs.type.label")}
          options={Object.values(TenderType)
            .filter(type => type !== TenderType.QuickOffer)
            .map((type) => ({
              value: type,
              label: t("common:tendertype." + type.toLowerCase())
            }))}
          className={cn(getInputWidth(leftPanelWidth))}
        />

        {tenderType === TenderType.Public && (
          <>
            <InputField
              form={form}
              name="description.platform"
              label={t("newtender.formgroups.description.inputs.platform.label")}
              placeholder={t("newtender.formgroups.description.inputs.platform.label")}
              className={cn(getInputWidth(leftPanelWidth))}
            />
            <InputField
              form={form}
              name="description.awardingAuthority"
              label={t("newtender.formgroups.description.inputs.awardingauthority.label")}
              placeholder={t("newtender.formgroups.description.inputs.awardingauthority.label")}
              className={cn(getInputWidth(leftPanelWidth))}
            />
          </>
        )}

        <RadioInputField
          form={form}
          name="description.customerType"
          label={t("newtender.formgroups.description.inputs.customertype.label")}
          options={[
            { value: CustomerType.Existingcustomer, label: t("newtender.formgroups.description.inputs.customertype.options.existingcustomer") },
            // TODO: CustomerType.ExistingCustomerNewSite, Waiting for Backend
            { value: CustomerType.Newservice, label: t("newtender.formgroups.description.inputs.customertype.options.existingandnewsite") },
            { value: CustomerType.Newcustomer, label: t("newtender.formgroups.description.inputs.customertype.options.newcustomer") },
          ]}
          className={cn(getInputWidth(leftPanelWidth))}
        />

        {/* Service type */}
        <div className={cn("form-item", leftPanelWidth < 52 ? "w-full" : "w-[48%]")}>
          <FormLabel className={cn(serviceTypeError && "text-red-500")}>
            {t("newtender.formgroups.description.inputs.servicetype.label")}
          </FormLabel>
          <div className="mt-2 space-y-2">
            <CheckboxInputField
              form={form}
              name="description.permanent"
              label={t("newtender.formgroups.description.inputs.servicetype.options.permanent")}
            />
            <CheckboxInputField
              form={form}
              name="description.mobile"
              label={t("newtender.formgroups.description.inputs.servicetype.options.mobile")}
            />
            <CheckboxInputField
              form={form}
              name="description.alarm"
              label={t("newtender.formgroups.description.inputs.servicetype.options.alarm")}
            />

            {serviceTypeError && (
              <FormMessage>
                {typeof serviceTypeMessage === "string" ? serviceTypeMessage : null}
              </FormMessage>
            )}
          </div>
        </div>

        <TextareaInputField
          form={form}
          name="description.details"
          label={t("newtender.formgroups.description.inputs.details.label")}
          placeholder={t("newtender.formgroups.description.inputs.details.placeholder")}
          showOptional
          formItemClassName={cn(getInputWidth(leftPanelWidth))}
          isFormItem={false}
        />

      </div>
    </div>
  );
}
